import { ArrowBack, Cancel, CheckCircle, Delete, Draw, Person, Print, Shield,
	Visibility } from "@mui/icons-material";
import { Button, Typography } from "@mui/material";
import { FC, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAlert } from "../../../components/contextProviders/AlertProvider";
import SignaturePad, { SignaturePadHandle } from "../../../components/epi/SignaturePad";
import { EPI_COLORS, complianceColors } from "../../../components/epi/epiColors";
import { EpiComplianceService } from "../../../services/EpiComplianceService";
import { EpiInspectionFlowService } from "../../../services/EpiInspectionFlowService";
import { EpiPdfService, blobToDataUrl, loadCompanyLogo } from "../../../utils/EpiPdfService";
import { StepProps } from "../EpiInspectionWizard";

const WORKER_DECLARATION =
	'Confirmo que participei da inspeção dos EPIs utilizados em minhas atividades e tomei ciência '
	+ 'das condições registradas, orientações recebidas e das ações necessárias identificadas.';

const RESPONSIBLE_DECLARATION =
	'Confirmo que realizei a inspeção dos EPIs, registrei as condições observadas, orientações '
	+ 'prestadas e as ações necessárias para correção das não conformidades.';

const EpiSignatureStep: FC<StepProps> = ({ inspection, onSave, onInspection }) => {
	const [workerBlob, setWorkerBlob] = useState<Blob | null>(null);
	const [responsibleBlob, setResponsibleBlob] = useState<Blob | null>(null);
	const [sending, setSending] = useState(false);
	const [printing, setPrinting] = useState(false);
	const workerPadRef = useRef<SignaturePadHandle>(null);
	const responsiblePadRef = useRef<SignaturePadHandle>(null);
	const navigate = useNavigate();
	const { showAlert, showError } = useAlert();
	const service = new EpiInspectionFlowService();
	const complianceService = new EpiComplianceService();
	const closed = inspection.status !== 'RASCUNHO';
	const cancelled = inspection.status === 'CANCELADA';
	const okCount = inspection.items.filter(item => item.status === 'OK').length;
	const notOkCount = inspection.items.filter(item => item.status === 'NAO_OK').length;
	const notApplicableCount = inspection.items.filter(item => item.status === 'NAO_APLICAVEL').length;
	const result = notOkCount ? 'NAO_CONFORME' : 'CONFORME';
	const resultColor = complianceColors[result] || EPI_COLORS.neutral;

	if (cancelled) {
		return (
			<div className="passo-assinaturas">
				<div className="painel conclusao">
					<Cancel style={{ color: EPI_COLORS.danger }} fontSize="large" />
					<Typography variant="h6">Inspeção cancelada</Typography>
					<span>Esta inspeção foi cancelada e não pode mais ser assinada.</span>
				</div>
			</div>
		);
	}

	return (
		<div className="passo-assinaturas">
			{!closed && (
				<div className="acoes-topo">
					<Button
						variant="outlined"
						startIcon={<ArrowBack />}
						disabled={inspection.workerSigned}
						onClick={() => onSave(2, inspection.items)}
					>
						VOLTAR
					</Button>
				</div>
			)}
			<div className="resumo-assinatura">
				<div className="painel caixa-resultado">
					<CheckCircle style={{ color: resultColor }} />
					<div>
						<span>Resultado da inspeção</span>
						<Typography variant="h6" style={{ color: resultColor }}>
							{result === 'NAO_CONFORME' ? 'NÃO CONFORME' : 'CONFORME'}
						</Typography>
						<span>
							{notOkCount === 1
								? '1 não conformidade identificada'
								: `${notOkCount} não conformidades identificadas`}
						</span>
					</div>
				</div>
				<div className="painel caixa-resumo">
					<Typography variant="subtitle1">Resumo</Typography>
					<p><strong>EPIs inspecionados: </strong>{inspection.items.length}</p>
					<p>
						<strong>OK: </strong>{okCount}
						<strong> | Não OK: </strong>{notOkCount}
						<strong> | N/A: </strong>{notApplicableCount}
					</p>
					<p style={{ color: inspection.acknowledged ? EPI_COLORS.success : EPI_COLORS.neutral }}>
						<strong>Ciência registrada: </strong>{inspection.acknowledged ? 'Sim' : 'Não'}
					</p>
				</div>
			</div>
			<div className="painel">
				<div className="titulo-assinatura" style={{ color: EPI_COLORS.info }}>
					<Person />
					<Typography variant="subtitle1">1. Assinatura do colaborador</Typography>
				</div>
				<div className="corpo-assinatura">
					<div className="coluna-canvas">
						<span className="instrucao">
							Solicite que o colaborador assine abaixo, confirmando sua participação na inspeção,
							ciência das informações apresentadas e das orientações recebidas.
						</span>
						<SignaturePad
							ref={workerPadRef}
							onChange={setWorkerBlob}
							disabled={sending || inspection.workerSigned}
						/>
					</div>
					<div className="coluna-declaracao">
						<div className="painel-declaracao">
							<Typography variant="subtitle2">Declaração do colaborador</Typography>
							<p>{WORKER_DECLARATION}</p>
							{inspection.workerSigned && (
								<span className="confirmado" style={{ color: EPI_COLORS.success }}>
									<CheckCircle fontSize="small" /> Ciência registrada.
								</span>
							)}
						</div>
						<div className="botoes-assinatura">
							<Button
								variant="outlined"
								startIcon={<Delete />}
								disabled={inspection.workerSigned}
								onClick={() => workerPadRef.current?.clear()}
							>
								LIMPAR ASSINATURA
							</Button>
							<Button
								variant="contained"
								startIcon={<CheckCircle />}
								disabled={!workerBlob || sending || inspection.workerSigned}
								style={!workerBlob || sending || inspection.workerSigned
									? undefined
									: { backgroundColor: EPI_COLORS.success }}
								onClick={() => send('TRABALHADOR', workerBlob)}
							>
								ASSINATURA CONFIRMADA
							</Button>
						</div>
					</div>
				</div>
			</div>
			<div className="painel">
				<div className="titulo-assinatura" style={{ color: EPI_COLORS.info }}>
					<Shield />
					<Typography variant="subtitle1">2. Assinatura do responsável pela inspeção</Typography>
				</div>
				<div className="corpo-assinatura">
					<div className="coluna-canvas">
						<span className="instrucao">
							Assine abaixo para confirmar que a inspeção foi realizada, as informações foram
							registradas de forma fidedigna e as orientações foram prestadas ao colaborador.
						</span>
						<SignaturePad
							ref={responsiblePadRef}
							onChange={setResponsibleBlob}
							disabled={sending || !inspection.workerSigned || closed}
						/>
					</div>
					<div className="coluna-declaracao">
						<div className="painel-declaracao">
							<Typography variant="subtitle2">Declaração do responsável</Typography>
							<p>{RESPONSIBLE_DECLARATION}</p>
							{inspection.responsibleSigned && (
								<span className="confirmado" style={{ color: EPI_COLORS.success }}>
									<CheckCircle fontSize="small" /> Assinatura registrada.
								</span>
							)}
						</div>
						<div className="botoes-assinatura">
							<Button
								variant="outlined"
								startIcon={<Delete />}
								disabled={!inspection.workerSigned || closed}
								onClick={() => responsiblePadRef.current?.clear()}
							>
								LIMPAR ASSINATURA
							</Button>
							<Button
								variant="contained"
								startIcon={<Draw />}
								disabled={!responsibleBlob || sending || !inspection.workerSigned || closed}
								style={!responsibleBlob || sending || !inspection.workerSigned || closed
									? undefined
									: { backgroundColor: EPI_COLORS.success }}
								onClick={() => send('RESPONSAVEL', responsibleBlob)}
							>
								FINALIZAR INSPEÇÃO
							</Button>
						</div>
					</div>
				</div>
			</div>
			{closed && (
				<div className="banner-sucesso" style={{ backgroundColor: EPI_COLORS.success }}>
					<CheckCircle fontSize="large" />
					<div>
						<Typography variant="subtitle1">Inspeção concluída com sucesso!</Typography>
						<span>
							Todos os registros foram salvos e as evidências de conformidade estão disponíveis.
						</span>
					</div>
					<div className="acoes-banner">
						<Button
							variant="outlined"
							startIcon={<Visibility />}
							onClick={() => navigate(`/inspecao-epis/inspecoes/${inspection.id}`)}
						>
							VISUALIZAR INSPEÇÃO
						</Button>
						<Button
							variant="outlined"
							startIcon={<Print />}
							disabled={printing}
							onClick={printReport}
						>
							IMPRIMIR RELATÓRIO
						</Button>
						<Button variant="contained" onClick={() => navigate('/inspecao-epis/nova')}>
							NOVA INSPEÇÃO
						</Button>
					</div>
				</div>
			)}
		</div>
	);

	async function printReport() {
		setPrinting(true);
		try {
			const dossierResponse = await complianceService.getDossier(inspection.id);
			if (!dossierResponse?.data) {
				showError('Não foi possível obter o dossiê da inspeção.');
				return;
			}
			const dossier = dossierResponse.data;
			const images: Record<string, string> = {};
			for (const name of dossier.evidenceFiles) {
				const fileResponse = await complianceService.getFile(inspection.id, name);
				if (fileResponse?.data) {
					images[name] = await blobToDataUrl(fileResponse.data as Blob);
				}
			}
			for (const signature of dossier.signatures) {
				const fileResponse = await complianceService.getFile(inspection.id, signature.fileName);
				if (fileResponse?.data) {
					images[signature.fileName] = await blobToDataUrl(fileResponse.data as Blob);
				}
			}
			new EpiPdfService().exportDossierToPdf(dossier, images, await loadCompanyLogo(dossier.inspection.companyId));
		} catch {
			showError('Não foi possível gerar o relatório.');
		} finally {
			setPrinting(false);
		}
	}

	async function send(role: string, blob: Blob | null) {
		if (!blob) {
			return;
		}
		setSending(true);
		try {
			const isWorker = role === 'TRABALHADOR';
			const form = new FormData();
			form.append('file', new File([blob], 'assinatura.png', { type: 'image/png' }));
			form.append('data', new Blob([JSON.stringify({
				role,
				signerName: isWorker ? inspection.employeeName : inspection.responsibleName,
				signerRegistration: isWorker ? inspection.employeeRegistration : '',
				signerPosition: isWorker ? inspection.positionName : '',
				declarationText: isWorker ? WORKER_DECLARATION : RESPONSIBLE_DECLARATION
			})], { type: 'application/json' }));
			const response = await service.sign(inspection.id, form);
			onInspection(response.data);
			showAlert(isWorker ? 'Assinatura registrada.' : 'Inspeção finalizada.');
		} catch (error: any) {
			showError(error?.response?.data || 'Não foi possível registrar a assinatura.');
		}
		setSending(false);
	}
};

export default EpiSignatureStep;
